import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isPaymentLinkExpired, type PaymentLink, type UserActivityEvent } from '@pay888/shared';
import { PageHeader } from '../components/PageHeader.js';
import { GettingStarted } from '../components/GettingStarted.js';
import { WalletOverview } from '../components/WalletUI.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { DepositBottomSheet } from '../components/DepositBottomSheet.js';
import { getActivityEventLabel } from '../features/activity/activityFormatters.js';
import {
  getListMyActivityEventsCallable,
  getListMyPaymentLinkDraftsCallable,
  isFirebaseConfigured,
} from '../services/firebase.js';

interface DashboardPageProps {
  readonly initialLinks?: readonly PaymentLink[];
  readonly initialEvents?: readonly UserActivityEvent[];
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ initialLinks, initialEvents }) => {
  const [links, setLinks] = useState<readonly PaymentLink[]>(() => initialLinks ?? []);
  const [events, setEvents] = useState<readonly UserActivityEvent[]>(() => initialEvents ?? []);
  const [loading, setLoading] = useState(
    () => isFirebaseConfigured() && (!initialLinks || !initialEvents)
  );
  const [error, setError] = useState<string | null>(null);
  const [isDepositOpen, setIsDepositOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isFirebaseConfigured() || (initialLinks && initialEvents)) return;

    let isMounted = true;

    const fetchDashboardData = async (): Promise<void> => {
      try {
        const [linksRes, eventsRes] = await Promise.all([
          initialLinks
            ? Promise.resolve({ data: initialLinks })
            : getListMyPaymentLinkDraftsCallable()(),
          initialEvents
            ? Promise.resolve({ data: initialEvents })
            : getListMyActivityEventsCallable()(),
        ]);

        if (isMounted) {
          setLinks(linksRes.data);
          setEvents(eventsRes.data);
        }
      } catch {
        if (isMounted) {
          setError('Failed to load dashboard overview. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [initialLinks, initialEvents]);

  const totalCount = links.length;
  const draftCount = links.filter((l) => l.status === 'draft').length;
  const inactiveCount = links.filter((l) => l.status === 'inactive').length;
  const publicCount = links.filter((l) => (l.visibility ?? 'private') === 'public').length;
  const recentEvents = events.slice(0, 5);
  const recentLinks = links.slice(0, 4);
  const isBrandNewUser = totalCount === 0 && events.length === 0;

  return (
    <div className="workspace-stack">
      <PageHeader
        title="Dashboard"
        description="View your balance, cards and recent activity."
        actions={
          <Link to="/settings" className="quiet-link">
            Account Settings ↗
          </Link>
        }
      />
      <WalletOverview onDeposit={() => setIsDepositOpen(true)} />
      <div className="launch-note" role="note">
        <p>
          KemyCard connection is in progress. Live payments and card funding are not available yet.
          Existing payment link drafts are preserved; payment collection is disabled.
        </p>
        <Link to="/preview">Explore the preview ↗</Link>
      </div>
      {error && (
        <p className="auth-form-error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <div className="panel" aria-busy="true">
          Loading your overview…
        </div>
      ) : isBrandNewUser ? (
        <GettingStarted />
      ) : (
        <>
          <div className="overview-metrics">
            {[
              ['Total Link Drafts', totalCount],
              ['Draft Status', draftCount],
              ['Publicly Shared', publicCount],
              ['Inactive Links', inactiveCount],
            ].map(([label, count]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
          <section className="panel">
            <div className="section-heading">
              <h2>Recent Payment Links</h2>
              <Link to="/receive/links" aria-label="Manage Payment Links">
                Manage links ↗
              </Link>
            </div>
            {recentLinks.map((link) => (
              <Link className="activity-row" key={link.id} to="/receive/links">
                <div>
                  <strong>{link.title}</strong>
                  <small>
                    {(link.visibility ?? 'private') === 'public' ? 'Public' : 'Private'} request
                  </small>
                </div>
                <StatusBadge
                  type={link.status === 'draft' ? 'dev' : 'disabled'}
                  label={isPaymentLinkExpired(link) ? 'EXPIRED' : link.status.toUpperCase()}
                />
              </Link>
            ))}
          </section>
          <section className="panel">
            <div className="section-heading">
              <h2>Recent Activity</h2>
              <Link to="/activity" aria-label="View all activity → View Full Activity Log">
                View all activity →
              </Link>
            </div>
            {recentEvents.length === 0 ? (
              <p className="muted">Your activity will appear here.</p>
            ) : (
              recentEvents.map((event) => (
                <div className="activity-row" key={event.id}>
                  <strong>{getActivityEventLabel(event.eventType)}</strong>
                  <small>{new Date(event.createdAt).toLocaleDateString()}</small>
                </div>
              ))
            )}
          </section>
        </>
      )}
      <DepositBottomSheet isOpen={isDepositOpen} onClose={() => setIsDepositOpen(false)} />
    </div>
  );
};
